import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { useRecoilState } from 'recoil';
import { Divider } from 'antd';
import { FlexColumn } from '@shared/ui/Containers/FlexContainer';
import { userSettingsAtom, USER_SETTINGS_DEFAULTS } from '@shared/state/siteState';
import useFirebase from '@app/providers/firebase';
import { AuthContext } from '@app/providers/firebase/AuthProvider';
import BooleanSetting from '../Components/BooleanSetting';
import ToggleSetting from '../Components/ToggleSetting';
import ScopedComponent from '@ui/Utility/ScopedComponent';
import { ref, set } from 'firebase/database';

const ComponentWrapper = styled(FlexColumn)`
  // height: calc(100% - 24px);
  max-height: 60vh;
  justify-content: flex-start;
  gap: 16px;
  overflow-y: scroll;
  padding-top: 24px;
`;

interface IComponent {
  reloadCallback: () => void;
}
const SettingsCenter = ({ reloadCallback }: IComponent) => {
  // @ts-ignore
  const { currentUser } = useContext(AuthContext);
  const { database } = useFirebase();
  const [userSettings, setUserSettings] = useRecoilState(userSettingsAtom);

  const toggleBoolean = async (key: string, value: boolean, reloadRequired: boolean) => {
    const settingsRef = ref(database, `/settings/${currentUser.email.split('@')[0]}/${key}`);
    await set(settingsRef, value);
    
    const booleanKey = key.replace('boolean/', '') as keyof typeof USER_SETTINGS_DEFAULTS.boolean;
    const currentSettings = userSettings ?? USER_SETTINGS_DEFAULTS;
    
    setUserSettings({
      ...currentSettings,
      boolean: {
        ...currentSettings.boolean,
        [booleanKey]: value,
      },
    });

    if (reloadRequired) reloadCallback();
  };

  const toggleProdDataInDev = async (key: string, value: boolean, reloadRequired: boolean) => {
    localStorage.setItem('useProdDataInDev', value ? 'true' : 'false');
    await toggleBoolean(key, value, reloadRequired);
  };

  const setToggleSetting = async (key: string, value: string, reloadRequired: boolean) => {
    const settingsRef = ref(database, `/settings/${currentUser.email.split('@')[0]}/${key}`);
    await set(settingsRef, value);
    
    const stringKey = key.replace('string/', '') as keyof typeof USER_SETTINGS_DEFAULTS.string;
    const currentSettings = userSettings ?? USER_SETTINGS_DEFAULTS;
    
    setUserSettings({
      ...currentSettings,
      string: {
        ...currentSettings.string,
        [stringKey]: value,
      },
    });

    if (reloadRequired) reloadCallback();
  };

  return (
    <ComponentWrapper>
      <Divider>Pricing</Divider>
      <BooleanSetting
        label="Show original pricing in price list"
        helpMessage="If the customer has a discount/increase, show the original price in the price list."
        storageKey="boolean/showOriginalPricing"
        value={userSettings?.boolean?.showOriginalPricing || false}
        callback={toggleBoolean}
        reloadRequired
      />
      <BooleanSetting
        label="[BETA] Predict material BOM based on part description"
        helpMessage="Use the part description to predict and/or correct the material BOM. This feature is in beta, so errors may occur."
        storageKey="boolean/predictMaterialBOM"
        value={userSettings?.boolean?.predictMaterialBOM || false}
        callback={toggleBoolean}
        reloadRequired={false}
      />
      <Divider>Sales Orders</Divider>
      <BooleanSetting
        label="[BETA] AI Description Validation"
        helpMessage="When saving a sales order, use AI to check if the description matches the order items (e.g., warns if description says 'bodies' but order contains necks). This feature is in beta."
        storageKey="boolean/enableAIDescriptionValidation"
        value={userSettings?.boolean?.enableAIDescriptionValidation || false}
        callback={toggleBoolean}
        reloadRequired={false}
      />
      <Divider>Production Schedule</Divider>
      <BooleanSetting
        label="Show CNC Load"
        helpMessage="Show the number of hours required on the CNC for the week's neck orders."
        storageKey="boolean/showCNCLoad"
        value={userSettings?.boolean?.showCNCLoad || false}
        callback={toggleBoolean}
        reloadRequired
      />
      <BooleanSetting
        label="Show Ash Tag"
        helpMessage="Show the Ash tag in the production schedule."
        storageKey="boolean/showAshTag"
        value={userSettings?.boolean?.showAshTag || false}
        callback={toggleBoolean}
        reloadRequired
      />
      <ToggleSetting
        label="Scroll to location"
        helpMessage="When changing the dates in the schedule, scroll to either the first date in the scheedule or the current week."
        options={[{ value: 'current', label: 'Current week' }, { value: 'start', label: 'Start' }]}
        value={userSettings?.string?.scrollToLocation || 'start'}
        storageKey="string/scrollToLocation"
        reloadRequired
        callback={setToggleSetting}
      />
      <Divider>Inventory</Divider>
      <BooleanSetting
        label="Show margin for inventory sell prices"
        helpMessage="Render inventory sell prices in three-color scale based on margin over cost."
        storageKey="boolean/showInventoryMargin"
        value={userSettings?.boolean?.showInventoryMargin ?? false}
        callback={toggleBoolean}
        reloadRequired
      />
      <BooleanSetting
        label="Show vendor description in inventory list if same"
        helpMessage="Show the vendor description even if it matches the item's description."
        storageKey="boolean/showSameDescription"
        value={userSettings?.boolean?.showSameDescription ?? false}
        callback={toggleBoolean}
        reloadRequired
      />
      <Divider>Utility</Divider>
      <BooleanSetting
        label="Show Help Highlights"
        helpMessage="Show highlights for help buttons."
        storageKey="boolean/showHelpHighlights"
        value={userSettings?.boolean?.showHelpHighlights ?? false}
        callback={toggleBoolean}
        reloadRequired
      />
      <BooleanSetting
        label="Show Decimal Weights, Density Calculator"
        helpMessage="Show decimal equivalent to weights displayed in the density calculator"
        storageKey="boolean/showDecimalWeights"
        value={userSettings?.boolean?.showDecimalWeights ?? false}
        callback={toggleBoolean}
        reloadRequired
      />
      <ScopedComponent requiredPermissions={['env.toggle']}>
        <BooleanSetting
          label="Show environment hop button"
          helpMessage="Show a floating button in the lower-left corner that opens the same page in Staging or Production."
          storageKey="boolean/showEnvHopButton"
          value={userSettings?.boolean?.showEnvHopButton ?? true}
          callback={toggleBoolean}
          reloadRequired={false}
        />
      </ScopedComponent>
      <ScopedComponent whitelist={['keithh@wildwoodmfg.com']}>
        <Divider>Experimental Features</Divider>
        <BooleanSetting
          label="Show Weighted Parts/day"
          helpMessage="Use the average part type price to show the relative number of parts per day, based on dollar amount."
          storageKey="boolean/useWeightedPartsPerDay"
          value={userSettings?.boolean?.useWeightedPartsPerDay ?? false}
          callback={toggleBoolean}
          reloadRequired
        />
        <BooleanSetting
          label="Dev Tools"
          helpMessage="Show development tools like database connection status"
          storageKey="boolean/showDevTools"
          value={userSettings?.boolean?.showDevTools ?? false}
          callback={toggleBoolean}
          reloadRequired
        />
      </ScopedComponent>
      <ScopedComponent whitelist={['keithh@wildwoodmfg.com']}>
        <Divider>Database</Divider>
        <BooleanSetting
          label="Use Production Data in Dev"
          helpMessage="When running locally, connect to the production database instead of dev-db. Requires page reload."
          storageKey="boolean/useProdDataInDev"
          value={userSettings?.boolean?.useProdDataInDev ?? false}
          callback={toggleProdDataInDev}
          reloadRequired
        />
      </ScopedComponent>
    </ComponentWrapper>
  );
};

export default SettingsCenter;
